import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[GlobalErrorBoundary] Uncaught runtime error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleClearCacheAndReload = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      sessionStorage.clear();
      localStorage.removeItem('chunk_retry_refreshed');
    } catch (e) {
      console.warn('[GlobalErrorBoundary] Failed to fully clear cache:', e);
    } finally {
      window.location.href = window.location.origin + '?t=' + Date.now();
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full bg-[#050505] text-white flex flex-col items-center justify-center p-6 select-none font-sans">
          <div className="max-w-md w-full bg-[#0d0d0d] border border-white/10 rounded-3xl p-8 flex flex-col items-center text-center shadow-2xl relative overflow-hidden">
            {/* Subtle glow effect */}
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-emerald-500/10 blur-3xl pointer-events-none rounded-full" />

            {/* Warning Icon */}
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-6 text-amber-400">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h2 className="text-xl font-bold tracking-tight text-white mb-2">
              일시적인 로딩 오류가 발생했습니다
            </h2>
            <p className="text-sm text-neutral-400 leading-relaxed mb-6">
              네트워크 불안정 또는 신규 업데이트 반영 과정에서 화면을 정상적으로 불러오지 못했습니다. 아래 버튼을 눌러 다시 시도해 주세요.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={this.handleReload}
                className="flex-1 px-4 py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-xl text-sm transition-all duration-150 shadow-lg shadow-emerald-500/20 active:scale-[0.98]"
              >
                페이지 새로고침
              </button>
              <button
                onClick={this.handleClearCacheAndReload}
                className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-xl text-sm transition-all duration-150 active:scale-[0.98]"
              >
                캐시 초기화 후 재접속
              </button>
            </div>

            {this.state.error && (
              <details className="mt-6 text-left w-full">
                <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-400 transition-colors">
                  오류 세부사항 확인
                </summary>
                <div className="mt-2 p-3 bg-black/50 border border-white/5 rounded-lg text-xs font-mono text-red-400/80 overflow-x-auto max-h-32">
                  {this.state.error.toString()}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
